import React, {useMemo, useState} from "react";
import {Alert, Button, Group, Modal, Select, Text} from "@mantine/core";
import {IconInfoCircle} from "@tabler/icons-react";
import useAllGalleries from "../../hooks/useAllGalleries";
import useMovePhotos from "./useMovePhotos";
import processAxiosError from "../../../../../utils/processAxiosError";

interface Props {
  opened: boolean
  onClose: () => void
  sourceId: number | null
  selectedPhotos: Set<number>
  selectedVideos: Set<number>
  onMoved: () => void
}

const MoveModal = ({opened, onClose, sourceId, selectedPhotos, selectedVideos, onMoved}: Props) => {
  const {data: galleries, isLoading} = useAllGalleries()
  const move = useMovePhotos()
  const [targetId, setTargetId] = useState<string | null>(null)

  const count = selectedPhotos.size + selectedVideos.size

  const options = useMemo(() =>
      (galleries ?? [])
        .filter(g => g.id !== sourceId)
        .map(g => ({value: String(g.id), label: `${g.title} (${g.date})`})),
    [galleries, sourceId])

  const handleClose = () => {
    if (move.isPending) return
    setTargetId(null)
    move.reset()
    onClose()
  }

  const handleMove = () => {
    if (!targetId) return
    move.mutate({
      sourceId,
      targetId: parseInt(targetId),
      photos: Array.from(selectedPhotos),
      videos: Array.from(selectedVideos),
    }, {
      onSuccess: () => {
        setTargetId(null)
        onMoved()
        onClose()
      }
    })
  }

  const error = processAxiosError(move.error)

  return (
    <Modal opened={opened} onClose={handleClose} title="Přesunout do galerie">
      <Text size="sm" mb="md">Vybráno {count} položek</Text>

      <Select
        data={options}
        value={targetId}
        onChange={setTargetId}
        placeholder={isLoading ? "Načítání galerií…" : "Vyber cílovou galerii"}
        searchable
        nothingFoundMessage="Nic nenalezeno"
        disabled={move.isPending}
        maxDropdownHeight={280}
      />

      {move.isError && error ? (
        <Alert variant="light" color="red" title={error.title} icon={<IconInfoCircle/>} mt="md">
          {error.text}
        </Alert>
      ) : null}

      <Group justify="right" gap={5} mt="md">
        <Button variant="light" color="gray" onClick={handleClose} disabled={move.isPending}>Zrušit</Button>
        <Button color="green" onClick={handleMove} disabled={!targetId} loading={move.isPending}>Přesunout</Button>
      </Group>
    </Modal>
  )
}

export default MoveModal
